"use client"

import { useState, useEffect } from "react"
import { View, StyleSheet, Text } from "react-native"
import { useTheme, ActivityIndicator } from "react-native-paper"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import { Zap, Radio, Route } from "lucide-react"
import { useAuth } from "../../contexts/AuthContext"
import { calculateGridMetrics, type GridMetricsResult } from "../../utils/gridMetrics"

interface GridMetrics {
  projects: Array<any>
}

interface GridAtGlanceSectionProps {
  metrics: GridMetrics
}

export function GridAtGlanceSection({ metrics }: GridAtGlanceSectionProps) {
  const theme = useTheme()
  const { apiEndpoint } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [projectsWithData, setProjectsWithData] = useState<any[]>([])
  const [gridMetrics, setGridMetrics] = useState<GridMetricsResult | null>(null)

  useEffect(() => {
    const fetchProjectsData = async () => {
      if (!apiEndpoint || !metrics.projects.length) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const projectDataPromises = metrics.projects.map(async (project) => {
          try {
            // First, fetch the list of files for this project
            const response = await fetch(project.url)
            if (!response.ok) {
              throw new Error(`Failed to fetch project files: ${response.statusText}`)
            }

            const directoryData = await response.json()

            // Find the Info.json file in the files list
            const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

            if (!infoFile) {
              // Info.json not found
              return null
            }

            // Fetch the Info.json file content
            const infoResponse = await fetch(infoFile.url)

            if (!infoResponse.ok) {
              throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
            }

            const infoData = await infoResponse.json()

            return {
              ...project,
              data: infoData,
            }
          } catch (err) {
            // Error fetching data
            return null
          }
        })

        const results = await Promise.all(projectDataPromises)
        const validProjects = results.filter(Boolean)
        setProjectsWithData(validProjects)

        // Calculate grid metrics
        const calculatedMetrics = calculateGridMetrics(validProjects)
        setGridMetrics(calculatedMetrics)
      } catch (err: any) {
        // Error fetching projects data
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProjectsData()
  }, [apiEndpoint, metrics.projects])

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
        <Text style={{ marginTop: 8, color: theme.colors.onSurfaceVariant }}>Loading grid data...</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={{ color: theme.colors.error }}>Error: {error}</Text>
      </View>
    )
  }

  if (!projectsWithData.length || !gridMetrics) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={{ color: theme.colors.onSurfaceVariant }}>No grid data available</Text>
      </View>
    )
  }

  const powerVsVoltageData = Object.values(gridMetrics.voltageGroups).map((group) => ({
    name: `${group.voltageLevel} kV`,
    value: group.totalPower,
    innerLabel: `${group.voltageLevel} kV`,
    outerLabel: `${group.totalPower} MW`,
  }))

  const lineCountData = Object.values(gridMetrics.voltageGroups).map((group) => ({
    name: `${group.voltageLevel} kV`,
    value: group.lineCount,
    innerLabel: `${group.voltageLevel} kV`,
    outerLabel: group.lineCount.toString(),
  }))

  const distanceData = Object.values(gridMetrics.voltageGroups).map((group) => ({
    name: `${group.voltageLevel} kV`,
    value: group.totalDistance,
    innerLabel: `${group.voltageLevel} kV`,
    outerLabel: `${Math.round(group.totalDistance)} km`,
  }))

  const COLORS = ["#2196F3", "#FFC107", "#9E9E9E", "#FF5722"]

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, payload }: any) => {
    const RADIAN = Math.PI / 180
    const innerRadius2 = innerRadius + (outerRadius - innerRadius) * 0.2
    const innerX = cx + innerRadius2 * Math.cos(-midAngle * RADIAN)
    const innerY = cy + innerRadius2 * Math.sin(-midAngle * RADIAN)

    const outerRadius2 = outerRadius * 1.1
    const outerX = cx + outerRadius2 * Math.cos(-midAngle * RADIAN)
    const outerY = cy + outerRadius2 * Math.sin(-midAngle * RADIAN)

    return (
      <>
        <text
          x={innerX}
          y={innerY}
          fill={theme.colors.onSurface}
          textAnchor={innerX > cx ? "start" : "end"}
          dominantBaseline="central"
          fontSize="12"
        >
          {payload.innerLabel}
        </text>
        <text
          x={outerX}
          y={outerY}
          fill={theme.colors.onSurfaceVariant}
          textAnchor={outerX > cx ? "start" : "end"}
          dominantBaseline="central"
          fontSize="12"
        >
          {payload.outerLabel}
        </text>
      </>
    )
  }

  const renderDonutChart = (data: any[], total: number | string, icon: any) => (
    <View style={styles.chartContainer}>
      <View style={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={70}
              fill="#8884d8"
              paddingAngle={2}
              dataKey="value"
              labelLine={false}
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.elevation.level2,
                borderColor: theme.colors.outline,
                borderRadius: 8,
              }}
              itemStyle={{
                color: theme.colors.onSurface,
              }}
              labelStyle={{
                color: theme.colors.onSurface,
                fontWeight: "bold",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </View>
      <View style={[styles.totalContainer, { backgroundColor: theme.colors.elevation.level1 }]}>
        {icon}
        <View style={styles.totalTextContainer}>
          <Text style={[styles.totalValue, { color: theme.colors.onSurface }]}>{total}</Text>
          <Text style={[styles.totalLabel, { color: theme.colors.onSurfaceVariant }]}>
            {typeof total === "number" && total === 1 ? "Line" : "Lines"}
          </Text>
        </View>
      </View>
    </View>
  )

  return (
    <View style={styles.container}>
      <View style={styles.chartsContainer}>
        {renderDonutChart(
          powerVsVoltageData,
          `${(gridMetrics.totalPower / 1000).toFixed(1)} GW`,
          <Zap size={24} color={theme.colors.primary} />,
        )}
        {renderDonutChart(lineCountData, gridMetrics.totalLines, <Radio size={24} color={theme.colors.primary} />)}
        {renderDonutChart(
          distanceData,
          `${Math.round(gridMetrics.totalDistance)} km`,
          <Route size={24} color={theme.colors.primary} />,
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: "auto",
  },
  chartsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  chartContainer: {
    flex: 1,
    minWidth: 220,
    alignItems: "center",
  },
  chartWrapper: {
    width: "100%",
    height: 200,
    position: "relative",
  },
  totalContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: -20,
    padding: 8,
    borderRadius: 20,
  },
  totalTextContainer: {
    alignItems: "center",
  },
  totalValue: {
    fontSize: 16,
    fontWeight: "bold",
  },
  totalLabel: {
    fontSize: 12,
    marginTop: 2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
})

